import { getFormatter } from '../Insights/KPI/utils'
import { cloneDeep, merge, uniqBy } from 'lodash'
import {
    AXIS_DEFAULT_VALUES,
    AXIS_TEXT_DEFAULT_VALUES,
    BAR_DEFAULT_VALUES,
    GAUGE_DEFAULT_VALUES,
    LABEL_DEFAULT_VALUES,
    LEGEND_DEFAULT_VALUES,
    LINE_DEFAULT_VALUES,
    PIE_DEFAULT_VALUES,
    RADIAL_DEFAULT_VALUES,
    TITLE_DEFAULT_VALUES,
} from '../screens/Explorer/DrawerTabs/StyleTab/styleConsts'
import { ScoopTheme } from '../Insights/Style'

const labelFormatter = (params, parsedSeries, summaryConfig) => {
    const formatOrEmpty = (value, short) => {
        const formattedVal = getFormatter(parsedSeries[params.seriesIndex]?.format, short)?.format(
            value
        )
        if (
            params?.data?.name &&
            (summaryConfig.seriesType === 'pie' || summaryConfig.seriesType === 'donut')
        )
            return `${params?.data?.name} \n ${formattedVal}`
        if (value === 0) return null
        return formattedVal
    }
    if (Array.isArray(params.data)) {
        if (params.data.length === 2) {
            return formatOrEmpty(params.data[1])
        } else if (params.data.length === 3) {
            return formatOrEmpty(params.data[2], true)
        } else {
            return formatOrEmpty(params.data[0])
        }
    } else if (params?.data?.value !== undefined) {
        return formatOrEmpty(params.data.value)
    } else {
        return formatOrEmpty(params.data) || ''
    }
}

const getTotalValuesBySeries = (categoryValue, parsedSeries, summaryConfig, summayData) => {
    let totalsBySeries = {}
    const isTimeCategory = summaryConfig.categoryAxis === 'Time'
    if (isTimeCategory) {
        parsedSeries.forEach((seriesItem) => {
            if (Array.isArray(seriesItem.data)) {
                seriesItem.data.forEach((item) => {
                    if (item[0] === categoryValue) {
                        totalsBySeries[seriesItem.metric] =
                            (totalsBySeries[seriesItem.metric] || 0) + item[1]
                    }
                })
            }
        })
    } else {
        parsedSeries.forEach((seriesItem) => {
            totalsBySeries[seriesItem.metric] =
                (totalsBySeries[seriesItem.metric] || 0) +
                seriesItem.data[summayData.categoryAxisValues.indexOf(categoryValue)]
        })
    }
    return totalsBySeries
}

const tooltipFormatter = (event, parsedSeries, summaryConfig, summaryData) => {
    if (!Array.isArray(event)) event = [event]
    if (summaryConfig.seriesType === 'scatter') {
        const firstEvent = event[0]
        if (!firstEvent || !firstEvent.data) return ''
        let s =
            firstEvent.marker +
            '<strong>' +
            (summaryConfig.categoryAxis === 'Time'
                ? summaryData.dates[firstEvent.dataIndex]
                : summaryData.categoryAxisValues[firstEvent.dataIndex]) +
            '</strong>' +
            (summaryData.categories?.length > 1 ? '</br>' + firstEvent.seriesName : '') +
            '</br>'
        if (
            parsedSeries[firstEvent.seriesIndex].formats &&
            parsedSeries[firstEvent.seriesIndex].formats.length === 2
        ) {
            s += getFormatter(parsedSeries[firstEvent.seriesIndex].formats[0]).format(
                firstEvent.data?.[0] || 0
            )
            s +=
                ', ' +
                getFormatter(parsedSeries[firstEvent.seriesIndex].formats[1]).format(
                    firstEvent.data?.[1] || 0
                )
        } else {
            s += getFormatter().format(firstEvent.data?.[0] || 0)
            s += ', ' + getFormatter().format(firstEvent.data?.[1] || 0)
        }
        return s
    } else {
        let s = ''
        if (
            (summaryConfig.seriesType === 'pie' || summaryConfig.seriesType === 'donut') &&
            summaryConfig.categoryAxis === 'Time'
        ) {
            let sharedLabel = ''
            event.forEach((item, index) => {
                if (item.data) {
                    if (index === 0) {
                        sharedLabel = '<strong>' + item.data.name + '</strong></br>'
                    }
                    s +=
                        item.marker +
                        getFormatter(parsedSeries[item.seriesIndex].format).format(
                            item.data.value
                        ) +
                        '</br>'
                }
            })
            s = sharedLabel + s
        } else if (summaryConfig.seriesType === 'heatmap') {
            const item = event[0]
            s +=
                item.marker +
                getFormatter(parsedSeries[item.seriesIndex].format).format(item.data[2]) +
                '</br>' +
                '<strong>' +
                item.name +
                '</strong></br>'
        } else {
            let sharedLabel = ''
            let seriesValues = ''
            event.forEach((item) => {
                if (item.data) {
                    let categoryLabel = Array.isArray(item.data)
                        ? item.data[0]
                        : Array.isArray(item.data?.value)
                          ? item.data.value[0]
                          : item.name

                    let value = Array.isArray(item.data)
                        ? item.data[1]
                        : Array.isArray(item.data?.value)
                          ? item.data.value[1]
                          : typeof item.data === 'number'
                            ? item.data
                            : item.data?.value
                    if (value !== undefined && value !== null) {
                        sharedLabel = '<strong>' + categoryLabel + '</strong></br>'
                        seriesValues +=
                            item.marker +
                            item.seriesName +
                            ': ' +
                            getFormatter(parsedSeries[item.seriesIndex].format).format(value) +
                            '</br>'
                    }
                }
            })
            if (summaryConfig.drillAttribute) {
                const category = Array.isArray(event[0].data)
                    ? event[0].data[0]
                    : Array.isArray(event[0].data?.value)
                      ? event[0].data.value[0]
                      : event[0].name

                let totalsBySeries = getTotalValuesBySeries(
                    category,
                    parsedSeries,
                    summaryConfig,
                    summaryData
                )
                const showSeriesNames =
                    summaryConfig.categoryAxis === 'Time' || Object.keys(totalsBySeries).length > 1

                Object.entries(totalsBySeries).forEach(([seriesName, total]) => {
                    seriesValues += showSeriesNames
                        ? `<strong>${seriesName}</strong> <strong>Total:</strong> ${getFormatter().format(total)}<br>`
                        : `<strong>Total:</strong> ${getFormatter().format(total)}<br>`
                })
            }
            s = sharedLabel + seriesValues
        }
        return s
    }
}

// base chart option
export const getBaseChartOption = (
    parsedSeries,
    parsedLegendData,
    theme,
    summaryConfig,
    summaryData
) => {
    return {
        animation: false,
        series: parsedSeries,
        color: theme?.colorScheme?.colors?.map((c) => c.val) || ScoopTheme.color,
        backgroundColor: theme?.colorScheme?.backgroundColor || '#FFFFFF',
        tooltip: {
            trigger: 'axis',
            axisPointer: {
                type: 'line',
                crossStyle: {
                    color: '#999',
                    width: 1,
                    type: 'dashed',
                },
            },
            order: 'valueDesc',
            confine: true,
            formatter: (event) => tooltipFormatter(event, parsedSeries, summaryConfig, summaryData),
        },
        legend: {
            orient: 'horizontal',
            top: 'bottom',
            data: parsedLegendData,
            tooltip: { show: true },
            itemGap: 10,
            show: summaryConfig.seriesType !== 'waterfall',
        },
        title: {
            text: summaryConfig.chartTitle,
            left: '50%',
        },
        label: {
            formatter: (params) => labelFormatter(params, parsedSeries, summaryConfig),
        },
    }
}

export const parseLegendData = (parsedSeries, summaryData) => {
    if (summaryData.categories) return parsedSeries.map((series) => ({ name: series.name }))
    else return []
}

export const setAxisTheme = (axis, color) => {
    axis.nameTextStyle = axis.nameTextStyle
        ? { ...axis.nameTextStyle, color: color }
        : { color: color }
    axis.axisLabel = axis.axisLabel ? { ...axis.axisLabel, color: color } : { color: color }
    axis.axisLine = axis.axisLine
        ? { ...axis.axisLine, lineStyle: { color: color } }
        : { lineStyle: { color: color } }
}

const ensureAxisOverrides = (optionAxis, overridesAxis, referenceAxis) => {
    if (optionAxis.length > overridesAxis.length) {
        optionAxis.forEach((_, i) => {
            overridesAxis[i] = overridesAxis[i] || { ...referenceAxis[0] }
        })
    }
}

export const ensureAxisOverridesAlignment = (option, overrides, summaryConfig) => {
    if (summaryConfig.seriesType === 'bar') {
        ensureAxisOverrides(option.yAxis, overrides.xAxis, overrides.xAxis)
        ensureAxisOverrides(option.xAxis, overrides.yAxis, overrides.yAxis)
    } else {
        ensureAxisOverrides(option.yAxis, overrides.yAxis, overrides.yAxis)
        ensureAxisOverrides(option.xAxis, overrides.xAxis, overrides.xAxis)
    }
}

const applyDefaultThemePreferences = (overrides, baseTheme) => {
    overrides.yAxis.forEach((axis, i) => {
        overrides.yAxis[i] = merge({}, baseTheme.yAxis, axis)
    })
    overrides.xAxis.forEach((axis, i) => {
        overrides.xAxis[i] = merge({}, baseTheme.xAxis, axis)
    })
    const { xAxis, yAxis, ...rest } = baseTheme
    return merge({}, rest, overrides)
}

const normalizeThemePreferences = (themePreferences) => {
    if (!Array.isArray(themePreferences.yAxis)) themePreferences.yAxis = [themePreferences.yAxis]
    if (!Array.isArray(themePreferences.xAxis)) themePreferences.xAxis = [themePreferences.xAxis]
}

const mergeAxisPreferences = (overrides, themePreferences) => {
    overrides.yAxis.forEach((axis, i) => {
        const preferenceToUse = themePreferences.yAxis[i] || themePreferences.yAxis[0]
        overrides.yAxis[i] = merge({}, preferenceToUse, axis)
    })
    overrides.xAxis.forEach((axis, i) => {
        const preferenceToUse = themePreferences.xAxis[i] || themePreferences.xAxis[0]
        overrides.xAxis[i] = merge({}, preferenceToUse, axis)
    })
}

const applyCustomThemePreferences = (overrides, theme) => {
    let themePreferences =
        typeof theme.chartPreferences === 'string'
            ? JSON.parse(theme.chartPreferences)
            : theme.chartPreferences
    normalizeThemePreferences(themePreferences)
    mergeAxisPreferences(overrides, themePreferences)
    const { xAxis, yAxis, ...rest } = themePreferences
    return merge({}, rest, overrides)
}

export const applyThemePreferences = (overrides, baseTheme, theme) => {
    let result = cloneDeep(overrides)
    if (theme?.chartPreferences) result = applyCustomThemePreferences(result, theme)
    else result = applyDefaultThemePreferences(result, baseTheme)
    return result
}

const handleRadialBarChart = (option, overrides, summaryData) => {
    option.angleAxis = {
        ...overrides.radialBar.angleAxis,
        data: cloneDeep(summaryData.categoryAxisValues),
    }
    option.polar = overrides.radialBar.polar
    option.radiusAxis = overrides.radialBar.radiusAxis
}

const handlePictorialBarChart = (option, overrides) => {
    const xAxisCopy = cloneDeep(option.xAxis)
    const yAxisCopy = cloneDeep(option.yAxis)
    // Swap axes if configured as bar
    if (overrides.pictorialBar.showAsBar) {
        option.xAxis = yAxisCopy
        option.yAxis = xAxisCopy
    }
}

const handleGaugeChart = (option, overrides) => {
    overrides.tooltip.show = false
    overrides.legend.show = false
}

const handleHeatmapChart = (option) => {
    const values = option.series[0].data
        .map((row) => row[2])
        .filter((value) => value !== null)
        .map((value) => Number(value))
    const maxValue = Math.max(...values)
    option.visualMap = {
        min: 0,
        max: maxValue,
        calculable: true,
        orient: 'horizontal',
        left: 'center',
        bottom: '5%',
    }
    option.grid.left = '5%'
    option.grid.top = '10%'
    option.grid.right = '5%'
    delete option.label.color
    option.xAxis[0].axisLabel.rotate = option.xAxis[0].data.length > 5 ? 45 : 0
    option.xAxis[0].axisLabel.fontSize = option.xAxis[0].data.length > 5 ? 12 : 14
    option.yAxis[0].axisLabel.fontSize = option.yAxis[0].data.length > 10 ? 12 : 14
    option.series[0].label.fontSize = option.xAxis[0].data.length > 5 ? 8 : 12
    option.series[0].label.rotate = option.xAxis[0].data.length > 5 ? 45 : 0
}

export const handleSpecializedCharts = (option, overrides, defaultTheme, summaryConfig) => {
    switch (summaryConfig.seriesType) {
        case 'radialBar':
            handleRadialBarChart(option, overrides)
            break
        case 'pictorialBar':
            handlePictorialBarChart(option, overrides)
            break
        case 'gauge':
            handleGaugeChart(option, overrides)
            break
        case 'heatmap':
            handleHeatmapChart(option)
            break
        default:
            return
    }
}

export const applyColumnResize = (
    summaryConfig,
    summaryData,
    option,
    overrides,
    categoryLegendData
) => {
    if (
        (summaryConfig.seriesType === 'bar' || summaryConfig.seriesType === 'column') &&
        categoryLegendData?.length > 1
    ) {
        let divisor = 1
        if (summaryData.categoryAxisValues > 1 && !summaryConfig.stacked)
            divisor = summaryData.categoryAxisValues?.length - 1
        if (categoryLegendData && !summaryConfig.stacked)
            divisor = divisor + categoryLegendData?.length - 1
        if (summaryConfig.selectedItems.length > 1 && summaryConfig.stacked) {
            const uniqueSeries = uniqBy(summaryConfig.selectedItems, 'kpi')
            divisor = uniqueSeries.length
        }
        const barGapPercentage = parseFloat(overrides.bar?.barGap?.split('%')[0] || '10')
        option.series.forEach((s, i) => {
            const availableSpace = 100 - (divisor - 1) * (barGapPercentage / divisor)
            let barWidth =
                (overrides.bar?.barWidth?.split('%')[0] || BAR_DEFAULT_VALUES.barWidth) / divisor
            barWidth = (barWidth * availableSpace) / 100
            if (barWidth) {
                option.series[i] = {
                    ...s,
                    ...overrides.bar,
                    barWidth: `${barWidth}%`,
                }
            }
        })
    } else if (summaryConfig.seriesTypeMap.size > 0) {
        const columnNames = Array.from(summaryConfig.seriesTypeMap.entries())
            .filter(([columnName, columnType]) => columnType === 'bar' || columnType === 'column')
            .map(([columnName]) => columnName)
        if (columnNames.length > 0) {
            const divisor = columnNames.length
            const barGapPercentage = parseFloat(overrides.bar?.barGap?.split('%')[0] || '10')
            option.series.forEach((s, i) => {
                const availableSpace = 100 - (divisor - 1) * barGapPercentage
                let barWidth =
                    (overrides.bar?.barWidth?.split('%')[0] || BAR_DEFAULT_VALUES.barWidth) /
                    divisor
                barWidth = (barWidth * availableSpace) / 100
                if (barWidth && columnNames.includes(s.name)) {
                    option.series[i] = {
                        ...s,
                        ...overrides.bar,
                        barWidth: `${barWidth}%`,
                    }
                }
            })
        }
    }
}

const adjustBorderRadius = (radius) => {
    const tempRadius = [...radius]
    return [tempRadius[2], tempRadius[0], tempRadius[1], tempRadius[3]]
}

export const handleBarChartRadius = (overrides, summaryConfig) => {
    if (summaryConfig.seriesType === 'bar') {
        const radius = overrides.bar?.itemStyle?.borderRadius || [5, 5, 0, 0]
        if (overrides.bar?.itemStyle?.borderRadius) {
            overrides.bar.itemStyle.borderRadius = adjustBorderRadius(radius)
        }
    }
}

export const cleanupPositionOverrides = (overrides) => {
    overrides.xAxis.forEach((axis) => {
        if (axis.position) delete axis.position
    })
    overrides.yAxis.forEach((axis) => {
        if (axis.position) delete axis.position
    })
}

export const configureTooltipsAndAxes = (overrides, summaryConfig) => {
    const circularChartTypes = ['pie', 'donut', 'gauge', 'radialBar']
    const isStackedBarColumn =
        summaryConfig.stacked &&
        (summaryConfig.seriesType === 'bar' || summaryConfig.seriesType === 'column')

    if (circularChartTypes.includes(summaryConfig.seriesType)) {
        // Hide axes for circular charts
        overrides.xAxis.forEach((axis) => (axis.show = false))
        overrides.yAxis.forEach((axis) => (axis.show = false))
        overrides.legend.icon = 'none'
        overrides.tooltip.trigger = 'item'
        if (overrides.grid?.tooltip?.axisPointer) overrides.grid.tooltip.trigger = 'item'
    }
    if (isStackedBarColumn) {
        overrides.tooltip.trigger = 'item'
        if (overrides.grid?.tooltip?.axisPointer) {
            overrides.grid.tooltip.trigger = 'item'
        }
    }
    if (summaryConfig.seriesType === 'heatmap') {
        overrides.tooltip.trigger = 'item'
        if (overrides.grid?.tooltip?.axisPointer) {
            overrides.grid.tooltip.trigger = 'item'
        }
    }
}

export const applyAxisDefaults = (overrides) => {
    overrides.yAxis.forEach((axis) => {
        if (axis.show === undefined) axis.show = AXIS_DEFAULT_VALUES.show
        if (axis.splitLine.show === undefined)
            axis.splitLine.show = AXIS_DEFAULT_VALUES.splitLine.show
        if (axis.splitLine.lineStyle.color === undefined)
            axis.splitLine.lineStyle.color = AXIS_DEFAULT_VALUES.splitLine.lineStyle.color
        if (axis.splitLine.lineStyle.type === undefined)
            axis.splitLine.lineStyle.type = AXIS_DEFAULT_VALUES.splitLine.lineStyle.type
        if (axis.splitLine.lineStyle.width === undefined)
            axis.splitLine.lineStyle.width = AXIS_DEFAULT_VALUES.splitLine.lineStyle.width
    })
}

const applyFontScale = (overrides, width, height, summaryConfig) => {
    overrides.title.textStyle.fontSize =
        (((overrides.title.textStyle.fontSize ?? TITLE_DEFAULT_VALUES.textStyle.fontSize) /
            overrides.dimensions.width) *
            width +
            ((overrides.title.textStyle.fontSize || TITLE_DEFAULT_VALUES.textStyle.fontSize) /
                overrides.dimensions.height) *
                height) /
        2

    overrides.legend.textStyle.fontSize =
        (((overrides.legend.textStyle.fontSize || LEGEND_DEFAULT_VALUES.textStyle.fontSize) /
            overrides.dimensions.width) *
            width +
            ((overrides.legend.textStyle.fontSize || LEGEND_DEFAULT_VALUES.textStyle.fontSize) /
                overrides.dimensions.height) *
                height) /
        2

    overrides.legend.itemSize =
        (((overrides.legend.itemSize || LEGEND_DEFAULT_VALUES.itemSize) /
            overrides.dimensions.width) *
            width +
            ((overrides.legend.itemSize || LEGEND_DEFAULT_VALUES.itemSize) /
                overrides.dimensions.height) *
                height) /
        2

    if (summaryConfig.seriesType === 'pie' || summaryConfig.seriesType === 'donut') {
        overrides.label.fontSize =
            (((overrides.label.fontSize || LABEL_DEFAULT_VALUES.fontSize) /
                overrides.dimensions.width) *
                width +
                ((overrides.label.fontSize || LABEL_DEFAULT_VALUES.fontSize) /
                    overrides.dimensions.height) *
                    height) /
            2
    }

    overrides.radialBar.radiusAxis.axisLabel.fontSize =
        (((overrides.radialBar.radiusAxis.axisLabel.fontSize ||
            RADIAL_DEFAULT_VALUES.radiusAxis.axisLabel.fontSize) /
            overrides.dimensions.width) *
            width +
            ((overrides.radialBar.radiusAxis.axisLabel.fontSize ||
                RADIAL_DEFAULT_VALUES.radiusAxis.axisLabel.fontSize) /
                overrides.dimensions.height) *
                height) /
        2

    overrides.radialBar.label.fontSize =
        (((overrides.radialBar.label.fontSize || RADIAL_DEFAULT_VALUES.label.fontSize) /
            overrides.dimensions.width) *
            width +
            ((overrides.radialBar.label.fontSize || RADIAL_DEFAULT_VALUES.label.fontSize) /
                overrides.dimensions.height) *
                height) /
        2

    overrides.gauge.detail.fontSize =
        (((overrides.gauge.detail.fontSize || GAUGE_DEFAULT_VALUES.detail.fontSize) /
            overrides.dimensions.width) *
            width +
            ((overrides.gauge.detail.fontSize || GAUGE_DEFAULT_VALUES.detail.fontSize) /
                overrides.dimensions.height) *
                height) /
        2

    overrides.gauge.title.fontSize =
        (((overrides.gauge.title.fontSize || GAUGE_DEFAULT_VALUES.title.fontSize) /
            overrides.dimensions.width) *
            width +
            ((overrides.gauge.title.fontSize || GAUGE_DEFAULT_VALUES.title.fontSize) /
                overrides.dimensions.height) *
                height) /
        2

    overrides.gauge.axisLabel.fontSize =
        (((overrides.gauge.axisLabel.fontSize || GAUGE_DEFAULT_VALUES.axisLabel.fontSize) /
            overrides.dimensions.width) *
            width +
            ((overrides.gauge.axisLabel.fontSize || GAUGE_DEFAULT_VALUES.axisLabel.fontSize) /
                overrides.dimensions.height) *
                height) /
        2

    overrides.xAxis.forEach((axis) => {
        axis.axisLabel.fontSize =
            (((axis.axisLabel.fontSize || AXIS_TEXT_DEFAULT_VALUES.fontSize) /
                overrides.dimensions.width) *
                width +
                ((axis.axisLabel.fontSize || AXIS_TEXT_DEFAULT_VALUES.fontSize) /
                    overrides.dimensions.height) *
                    height) /
            2

        axis.nameTextStyle.fontSize =
            (((axis.nameTextStyle.fontSize || AXIS_TEXT_DEFAULT_VALUES.fontSize) /
                overrides.dimensions.width) *
                width +
                ((axis.nameTextStyle.fontSize || AXIS_TEXT_DEFAULT_VALUES.fontSize) /
                    overrides.dimensions.height) *
                    height) /
            2
    })

    overrides.yAxis.forEach((axis) => {
        axis.axisLabel.fontSize =
            (((axis.axisLabel.fontSize || AXIS_TEXT_DEFAULT_VALUES.fontSize) /
                overrides.dimensions.width) *
                width +
                ((axis.axisLabel.fontSize || AXIS_TEXT_DEFAULT_VALUES.fontSize) /
                    overrides.dimensions.height) *
                    height) /
            2

        axis.nameTextStyle.fontSize =
            (((axis.nameTextStyle.fontSize || AXIS_TEXT_DEFAULT_VALUES.fontSize) /
                overrides.dimensions.width) *
                width +
                ((axis.nameTextStyle.fontSize || AXIS_TEXT_DEFAULT_VALUES.fontSize) /
                    overrides.dimensions.height) *
                    height) /
            2
    })
}

export const applyFontScaling = (overrides, embeddedSizeProps, summaryConfig) => {
    if (embeddedSizeProps) {
        const { height, width } = embeddedSizeProps.containerSize
        applyFontScale(overrides, width, height, summaryConfig)
    }
}

const applyStackBorderRadius = (stackSeries, overrides) => {
    const radiusSeries = []
    for (let i = 0; i < stackSeries[0]?.data.length; i++) {
        let lastValidIndex = -1
        stackSeries.forEach((series, j) => {
            const value = Array.isArray(series.data[i]) ? series.data[i][1] : series.data[i]
            if (value !== 0 && value !== null) lastValidIndex = j
        })
        if (lastValidIndex !== -1) {
            radiusSeries.push({ data: i, series: lastValidIndex })
        }
    }
    stackSeries.forEach((series, index) => {
        series.data = series.data.map((d, i) => {
            const isTop = radiusSeries.some((r) => r.data === i && r.series === index)
            const value = typeof d === 'object' ? d?.value || d : d
            const itemStyle = isTop
                ? { borderRadius: overrides.bar?.itemStyle?.borderRadius || [5, 5, 0, 0] }
                : { borderRadius: [0, 0, 0, 0] }
            return { value, itemStyle }
        })
    })
}

export const handleStackedChartStyling = (option, overrides, summaryConfig) => {
    const isStackedBarColumn =
        summaryConfig.stacked &&
        (summaryConfig.seriesType === 'bar' || summaryConfig.seriesType === 'column') &&
        summaryConfig.drillAttribute
    if (!isStackedBarColumn) return
    const stacks = {}
    option.series.forEach((series, i) => {
        const stackName = series.stack || `stack_${i}`
        if (!stacks[stackName]) stacks[stackName] = []
        const hasValidData = Array.isArray(series.data)
            ? series.data.some((d) => d !== 0 && d !== null)
            : series.data !== 0 && series.data !== null
        if (hasValidData) stacks[stackName].push(series)
    })
    Object.values(stacks).forEach((stackSeries) => {
        applyStackBorderRadius(stackSeries, overrides)
    })
}

const flipBorderRadius = (defaultRadius, seriesType) => {
    const tempRadius = [...defaultRadius]
    if (seriesType === 'bar') {
        return [tempRadius[1], tempRadius[0], tempRadius[3], tempRadius[2]]
    } else {
        return [tempRadius[2], tempRadius[3], tempRadius[0], tempRadius[1]]
    }
}

const extractValue = (d) => {
    if (Array.isArray(d)) {
        return typeof d[1] === 'number' ? d[1] : d[0]
    } else if (typeof d === 'object' && d !== null) {
        return d.value
    } else {
        return d
    }
}

const handleStandardNegativeRadius = (option, summaryConfig) => {
    option.series.forEach((s) => {
        const defaultRadius = s.itemStyle?.borderRadius || [5, 5, 0, 0]
        s.data = s.data.map((d) => {
            const value = extractValue(d)
            const adjustedRadius = [...defaultRadius]
            if (value < 0) {
                // Flip border radius for negative values
                const flippedRadius = flipBorderRadius(defaultRadius, summaryConfig.seriesType)
                return {
                    value,
                    itemStyle: {
                        ...d?.itemStyle,
                        borderRadius: flippedRadius,
                    },
                }
            }
            return {
                value,
                itemStyle: {
                    ...d?.itemStyle,
                    borderRadius: adjustedRadius,
                },
            }
        })
    })
}

const handleStackedNegativeRadius = (option, summaryConfig) => {
    option.series.forEach((s, i) => {
        const defaultRadius = s.itemStyle?.borderRadius || [5, 5, 0, 0]
        s.data = s.data.map((d, j) => {
            const value = extractValue(d)
            if (value < 0 && d?.itemStyle?.borderRadius !== [0, 0, 0, 0]) {
                d.itemStyle.borderRadius = flipBorderRadius(defaultRadius, summaryConfig.seriesType)
                const fixRoundedTopIndex = i - 1
                if (fixRoundedTopIndex >= 0) {
                    const fixTopData = option.series[fixRoundedTopIndex].data[j]
                    const fixedValue = extractValue(fixTopData)
                    const adjustedFixedRadius =
                        summaryConfig.seriesType === 'bar'
                            ? [
                                  defaultRadius[3],
                                  defaultRadius[1],
                                  defaultRadius[1],
                                  defaultRadius[0],
                              ]
                            : defaultRadius
                    option.series[fixRoundedTopIndex].data[j] = {
                        value: fixedValue,
                        itemStyle: { borderRadius: adjustedFixedRadius },
                    }
                }
            }
            return {
                value,
                itemStyle: { borderRadius: d?.itemStyle?.borderRadius || [5, 5, 0, 0] },
            }
        })
    })
}

const adjustNegativeValueBorderRadius = (option, overrides, summaryConfig) => {
    const isStackedDrillable =
        summaryConfig.stacked &&
        (summaryConfig.seriesType === 'bar' || summaryConfig.seriesType === 'column') &&
        summaryConfig.drillAttribute
    if (isStackedDrillable) {
        handleStackedNegativeRadius(option, overrides)
    } else {
        handleStandardNegativeRadius(option)
    }
}

const dataContainsNegatives = (option) => {
    return option.series.some((s) => {
        return s.data.some((d) => {
            if (typeof d === 'number') {
                return d < 0
            } else if (d && Array.isArray(d) && d.length > 1) {
                return typeof d[1] === 'number' ? d[1] < 0 : d[0] < 0
            } else if (typeof d === 'object' && d !== null) {
                return d.value < 0
            }
            return false
        })
    })
}

export const handleNegativeValues = (option, overrides, summaryConfig) => {
    if (!dataContainsNegatives(option)) return
    const targetAxis = summaryConfig.seriesType === 'bar' ? 'xAxis' : 'yAxis'
    option[targetAxis].forEach((axis) => {
        axis.min = function (value) {
            const absMax = Math.max(Math.abs(value.min), Math.abs(value.max))
            return -Math.ceil(absMax)
        }
        axis.max = function (value) {
            const absMax = Math.max(Math.abs(value.min), Math.abs(value.max))
            return Math.ceil(absMax)
        }
    })
    adjustNegativeValueBorderRadius(option, overrides)
}

export const applyLegendAndTextLimits = (option, overrides, summaryConfig, slack) => {
    const max = overrides.legend?.maxLegends || LEGEND_DEFAULT_VALUES.maxLegends
    const maxChars =
        overrides.legend?.textStyle.maxCharacters || LEGEND_DEFAULT_VALUES.textStyle.maxCharacters
    const maxAxisLabelChars =
        overrides.xAxis[0]?.axisLabel?.maxCharacters || AXIS_TEXT_DEFAULT_VALUES.maxCharacters
    if (option.legend?.data?.length > 0) {
        option.legend.formatter = (s) =>
            s.length > maxChars ? s.substring(0, maxChars) + '...' : s
        if (max !== 'all') option.legend.data = option?.legend?.data?.slice(0, max)
    }
    const targetAxis = summaryConfig.seriesType === 'bar' ? 'yAxis' : 'xAxis'
    option[targetAxis] = option[targetAxis].map((axis) => ({
        ...axis,
        axisLabel: {
            ...axis.axisLabel,
            formatter: (s) =>
                s.length > maxAxisLabelChars ? s.substring(0, maxAxisLabelChars) + '...' : s,
        },
    }))
    if (slack) {
        if (option.title.top === '0%') option.title.top = '5%'
        if (option.legend.top === 'bottom') option.legend.top = '95%'
    }
}

export const applyLegendPositioning = (option, overrides) => {
    const legendPosition = overrides.legend?.scoopPosition || 'bottom'
    const positionConfigs = {
        top: {
            grid: { top: '20%', bottom: '5%', left: '10%', right: '10%' },
            legend: { left: 'center', top: '10%', orient: 'horizontal' },
        },
        bottom: {
            grid: { top: '15%', bottom: '15%', left: '10%', right: '10%' },
            legend: { left: 'center', top: 'bottom', orient: 'horizontal' },
        },
        left: {
            grid: { top: '10%', bottom: '5%', left: '20%', right: '10%' },
            legend: { left: 'left', top: 'middle', orient: 'vertical' },
        },
        right: {
            grid: { top: '10%', bottom: '5%', left: '10%', right: '20%' },
            legend: { left: 'right', top: 'middle', orient: 'vertical' },
        },
    }
    const config = positionConfigs[legendPosition]
    if (config) {
        option.grid = merge({}, option.grid, config.grid)
        option.legend = merge({}, option.legend, config.legend)
    }
    if (!option.legend.textStyle) option.legend.textStyle = {}
    option.legend.textStyle.padding = [0, 0, 0, -3]
}

export const handleChartSorting = (option, summaryConfig) => {
    if (!summaryConfig.sortColumns?.length) return

    const hasSorting = summaryConfig.sortColumns.some((sortingOption) =>
        ['asc', 'desc'].includes(sortingOption.order)
    )
    if (hasSorting && summaryConfig.seriesType === 'bar') {
        option.series.forEach((series) => {
            if (series.data) series.data = series.data.toReversed()
        })
        option.yAxis.forEach((axis) => {
            if (axis.data) axis.data = axis.data.toReversed()
        })
    }
}

const applyStringBreak = (str) => {
    if (!str || typeof str !== 'string') return str
    const middle = Math.floor(str.length / 2)
    let leftPos = str.lastIndexOf(' ', middle)
    let rightPos = str.indexOf(' ', middle)
    if (leftPos === -1) leftPos = 0
    if (rightPos === -1) rightPos = str.length
    const breakPoint = middle - leftPos <= rightPos - middle ? leftPos : rightPos
    if (breakPoint > 0 && breakPoint < str.length) {
        return str.slice(0, breakPoint) + '\n' + str.slice(breakPoint + 1)
    } else {
        return str.slice(0, middle) + '\n' + str.slice(middle)
    }
}

export const handleAxisTitles = (option, summaryConfig, slack) => {
    if (summaryConfig.selectedItems.length > 4) {
        if (summaryConfig.seriesType === 'bar') {
            option.xAxis.forEach((axis) => {
                axis.name = ''
            })
        }
        if (['column', 'line', 'area'].includes(summaryConfig.seriesType)) {
            option.yAxis.forEach((axis) => {
                axis.name = ''
            })
        }
    } else {
        if (summaryConfig.seriesType === 'bar') {
            option.xAxis.forEach((axis) => {
                if (slack) {
                    axis.nameLocation = 'middle'
                    axis.nameGap = 40
                } else {
                    axis.name = axis.name.split(' ').join('\n')
                }
            })
        }
        if (['column', 'line', 'area'].includes(summaryConfig.seriesType)) {
            option.yAxis.forEach((axis) => {
                if (axis.name.includes('\n') && slack) {
                    axis.nameLocation = 'middle'
                    axis.nameGap = 120
                }
            })
            if (option.title.text.split(' ').length > 3 && !slack) {
                option.title.text = applyStringBreak(option.title.text)
                option.grid.top = '20%'
            }
        }
    }
}

export const applySeriesLabels = (option, summaryConfig) => {
    for (const s of option.series) {
        if (summaryConfig.seriesType === 'column' || summaryConfig.seriesType === 'bar') {
            s.label = {
                position:
                    summaryConfig.styleOverrides?.bar?.position || BAR_DEFAULT_VALUES.position,
            }
        } else if (summaryConfig.seriesType === 'line') {
            s.label = {
                position:
                    summaryConfig.styleOverrides?.line?.position || LINE_DEFAULT_VALUES.position,
            }
        } else if (summaryConfig.seriesType === 'pie' || summaryConfig.seriesType === 'donut') {
            s.label = {
                position:
                    summaryConfig.styleOverrides?.pie?.position || PIE_DEFAULT_VALUES.position,
                show: summaryConfig.styleOverrides?.label?.show ?? LABEL_DEFAULT_VALUES.show,
            }
        }
    }
}

export const applyLegendIconSizeTransformation = (option) => {
    const size = option.legend.itemSize || 10
    option.legend.itemWidth = size
    option.legend.itemHeight = size
    delete option.legend.itemSize
}
